import { useTheme } from '../theme/ThemeContext';
import { useState, useEffect, useRef } from 'react';
import Button from './widgets/button';
import Dropdown from './widgets/dropdown';
import PercentStackedBarchart from './widgets/percentStackedBarchart.jsx'

export default function SubSampleTaxaDistribution({ multipleSamplesMetaData, multipleSamplesTaxaDataIndividual, isLoadingIndividualTaxa, points, ids }) {
    const { theme } = useTheme();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [rank, setRank] = useState("o");
    const [barchartData, setBarchartData] = useState([]);
    const [sortMethod, setSortMethod] = useState("abundance");
    const abortControllerRef = useRef(null);
    // percent stacked barchart 

    useEffect(() => {
        if (!multipleSamplesTaxaDataIndividual) return;

        setIsLoading(true);

        try {
            // extract taxonomic features for the selected rank
            const features = multipleSamplesTaxaDataIndividual.map(sample => {
                const taxaAtRank = sample.taxonomic_data.filter(t => t.taxon.rank === rank);

                const abundances = new Map();
                taxaAtRank.forEach(t => {
                    abundances.set(t.taxon.name, t.relative_abundance);
                });

                return abundances;
            });

            const formattedData = multipleSamplesTaxaDataIndividual.map((sample, index) => {
                const taxonomic_data = [];

                const taxaMap = features[index];

                taxaMap.forEach((abundance, taxonName) => {
                    taxonomic_data.push({
                        taxon: {
                            name: taxonName
                        },
                        relative_abundance: abundance
                    });
                });

                const sampleId =
                    (multipleSamplesMetaData && multipleSamplesMetaData[index])
                        ? multipleSamplesMetaData[index].run_assembly_id
                        : (sample.sample_id || `Sample ${index + 1}`);

                return {
                    sample_id: sampleId,
                    display_name: sampleId,
                    taxonomic_data: taxonomic_data
                };
            });

            setBarchartData(formattedData);
            setError(null);

        } catch (err) {
            console.error('Barchart data processing error:', err);
            setError('Failed to process taxonomic data for barchart');
        } finally {
            setIsLoading(false);
        }

    }, [multipleSamplesTaxaDataIndividual, multipleSamplesMetaData, rank]);


    const handleRankSelection = (item) => {
        const rankDict = {
            'phylum': 'p',
            'class': 'c',
            'order': 'o',
            'family': 'f',
            'genus': 'g',
            'species': 's'
        };
        setRank(rankDict[item]);
    };

    const handleSortMethodChange = (newSortMethod) => {
        setSortMethod(newSortMethod);
    };

    const getButtonStyle = (isActive) => ({
        minHeight: '36px',
        fontSize: '14px',
        padding: '4px 10px',
        margin: '0 5px',
        width: 'auto',
        flex: 1,
        backgroundColor: isActive ? theme.colors.primary : 'transparent',
        color: isActive ? theme.colors.textContrast : theme.colors.text,
        fontWeight: isActive ? 'bold' : 'normal'
    });

    const buttonStyle = {
        padding: '6px 12px',
        margin: '0 5px',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        backgroundColor: theme.colors.secondary,
        color: theme.colors.textContrast,
        transition: 'background-color 0.2s'
    };

    const activeButtonStyle = {
        ...buttonStyle,
        backgroundColor: theme.colors.primary,
        fontWeight: 'bold'
    };

    return (
        <div>
            {error && <p style={{ color: 'red' }}>{error}</p>}

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                margin: '10px 0',
                color: theme.colors.text,
            }}>
                <Dropdown
                    items={['phylum', 'class', 'order', 'family', 'genus', 'species']}
                    onSelect={handleRankSelection}
                    placeholder="order"
                    fontSize='14px'
                />
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                margin: '10px 0',
                color: theme.colors.text,
            }}>
                <Button
                    onClick={() => setSortMethod("abundance")}
                    style={getButtonStyle(sortMethod === "abundance")}
                >
                    Abundance
                </Button>
                <Button
                    onClick={() => setSortMethod("x")}
                    disabled={!points || !ids}
                    style={getButtonStyle(sortMethod === "x")}
                >
                    X-axis
                </Button>
                <Button
                    onClick={() => setSortMethod("y")}
                    disabled={!points || !ids}
                    style={getButtonStyle(sortMethod === "y")}
                >
                    Y-axis
                </Button>
            </div>

            {isLoading ? (
                <p>Making barchart</p>
            ) : barchartData ? (
                <PercentStackedBarchart
                    data={barchartData}
                    points={points}
                    ids={ids}
                    sortMethod={sortMethod}
                    onSortMethodChange={handleSortMethodChange} />
            ) : (
                <p> No data available </p>
            )}
        </div>
    );
}